// Moved verbatim out of the original single-file App.tsx. Markup, classes, copy
// and localStorage keys are unchanged — the meeting tool is live, and the
// storage key in particular must keep hashing the same way or every saved
// checkbox state on every existing user's machine is orphaned.

import { useEffect, useMemo, useState } from 'react';
import type { ExtractResult } from '../../lib/briefblip';
import { resultToIcs } from '../../lib/ical';
import { resultToMarkdown } from '../../lib/markdown';
import { Card } from './Card';
import { MeetingMeta } from './MeetingMeta';
import { TodoList, type TodoStatus } from './TodoList';

// Derive a stable per-meeting localStorage key from the brief's title/recap so
// checkbox state is restored when the same brief is generated again.
function meetingStorageKey(result: ExtractResult): string {
  const base = (result.title || result.recap || 'meeting').toLowerCase();
  let h = 0;
  for (let i = 0; i < base.length; i++) h = (h * 31 + base.charCodeAt(i)) | 0;
  return `briefblip:todos:${(h >>> 0).toString(36)}`;
}

function useMeetingTodos(storageKey: string) {
  const read = (): Record<string, TodoStatus> => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Record<string, TodoStatus>) : {};
    } catch {
      return {};
    }
  };
  const [states, setStates] = useState<Record<string, TodoStatus>>(read);
  useEffect(() => setStates(read()), [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = (itemKey: string, status: TodoStatus) => {
    setStates((prev) => {
      const next = { ...prev };
      if (status === 'open') delete next[itemKey];
      else next[itemKey] = status;
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // storage blocked - state still works in-session
      }
      return next;
    });
  };
  return { states, setStatus };
}

export function Results({ result }: { result: ExtractResult }) {
  const [copied, setCopied] = useState(false);
  const storageKey = useMemo(() => meetingStorageKey(result), [result]);
  const { states, setStatus } = useMeetingTodos(storageKey);

  const allKeys = useMemo(() => {
    const keys: string[] = [];
    result.decisions.forEach((t) => keys.push(`decision::${t}`));
    result.action_items.forEach((a) => keys.push(`action::${a.text}`));
    result.next_steps.forEach((t) => keys.push(`next::${t}`));
    return keys;
  }, [result]);
  const doneCount = allKeys.filter((k) => states[k] === 'done').length;
  const asideCount = allKeys.filter((k) => states[k] === 'aside').length;

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(resultToMarkdown(result));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard blocked - no-op
    }
  };

  const downloadMarkdown = () => {
    const blob = new Blob([resultToMarkdown(result)], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = (result.title || 'meeting-brief')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 60) || 'meeting-brief';
    a.href = url;
    a.download = `${slug}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadIcs = () => {
    const blob = new Blob([resultToIcs(result)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = (result.title || 'meeting-brief')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 60) || 'meeting-brief';
    a.href = url;
    a.download = `${slug}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="mt-8 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-mono text-lg font-bold text-[#17202c]">
            {result.title || 'Meeting brief'}
          </h2>
          {(doneCount > 0 || asideCount > 0) && (
            <p className="mt-0.5 font-mono text-xs text-[#7a8493]">
              {doneCount} done - {asideCount} set aside - {allKeys.length - doneCount - asideCount} open
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={copyMarkdown}
            className="rounded-md border border-edge px-3 py-1.5 text-xs text-cyan transition hover:bg-cyan/10"
          >
            {copied ? 'Copied!' : 'Copy markdown'}
          </button>
          <button
            onClick={downloadMarkdown}
            className="rounded-md border border-edge px-3 py-1.5 text-xs text-cyan transition hover:bg-cyan/10"
          >
            Export .md
          </button>
          <button
            onClick={downloadIcs}
            className="rounded-md border border-edge px-3 py-1.5 text-xs text-cyan transition hover:bg-cyan/10"
          >
            Export .ics
          </button>
        </div>
      </div>

      <MeetingMeta result={result} />

      {result.recap && (
        <Card title="Recap" count={null}>
          <p className="text-sm leading-relaxed text-[#5f6978]">{result.recap}</p>
        </Card>
      )}

      <Card title="Decisions" count={result.decisions.length}>
        <TodoList
          listType="decision"
          items={result.decisions.map((t) => ({ text: t }))}
          states={states}
          onChange={setStatus}
          empty="No decisions detected."
        />
      </Card>

      <Card title="Action items" count={result.action_items.length}>
        <TodoList
          listType="action"
          items={result.action_items}
          states={states}
          onChange={setStatus}
          empty="No action items detected."
        />
      </Card>

      <Card title="Next steps" count={result.next_steps.length}>
        <TodoList
          listType="next"
          items={result.next_steps.map((t) => ({ text: t }))}
          states={states}
          onChange={setStatus}
          empty="No next steps detected."
        />
      </Card>

      {result.model && (
        <p className="pt-1 text-center font-mono text-[0.7rem] text-[#8b94a3]">
          Generated by {result.model}
          {result.cost && ` - ~$${formatCost(result.cost.usd)}`}
        </p>
      )}
    </section>
  );
}

function formatCost(usd: number): string {
  if (usd <= 0) return '0';
  if (usd < 0.01) return usd.toFixed(4);
  return usd.toFixed(2);
}
